/**
 * TanStack Query hooks for storage connection management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  storageConnectionsApi,
  siteStorageApi,
  type CreateStorageConnectionRequest,
} from '../api';
import { siteKeys } from './useSites';

// ============================================================================
// Query Keys
// ============================================================================

export const storageConnectionKeys = {
  all: ['storage-connections'] as const,
  list: () => [...storageConnectionKeys.all, 'list'] as const,
  detail: (id: number) => [...storageConnectionKeys.all, 'detail', id] as const,
};

// ============================================================================
// Storage Connection Hooks
// ============================================================================

/** Fetch all storage connections */
export function useStorageConnections() {
  return useQuery({
    queryKey: storageConnectionKeys.list(),
    queryFn: () => storageConnectionsApi.list(),
  });
}

/** Fetch a single storage connection */
export function useStorageConnection(id: number) {
  return useQuery({
    queryKey: storageConnectionKeys.detail(id),
    queryFn: () => storageConnectionsApi.get(id),
    enabled: !!id,
  });
}

/** Create a new storage connection */
export function useCreateStorageConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStorageConnectionRequest) =>
      storageConnectionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storageConnectionKeys.list(),
      });
    },
  });
}

/** Delete a storage connection */
export function useDeleteStorageConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => storageConnectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storageConnectionKeys.list(),
      });
    },
  });
}

/** Test a saved storage connection */
export function useTestStorageConnection() {
  return useMutation({
    mutationFn: (id: number) => storageConnectionsApi.test(id),
  });
}

/** Test connection details without saving (inline test) */
export function useTestInlineStorageConnection() {
  return useMutation({
    mutationFn: (data: CreateStorageConnectionRequest) =>
      storageConnectionsApi.testInline(data),
  });
}

// ============================================================================
// Site Storage Hooks
// ============================================================================

/** Update a site's storage connection */
export function useUpdateSiteStorage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteSlug,
      storageConnectionId,
    }: {
      siteSlug: string;
      storageConnectionId: number | null;
    }) => siteStorageApi.updateSiteStorage(siteSlug, storageConnectionId),
    onSuccess: (_, variables) => {
      // Invalidate site data to refresh storage connection info
      queryClient.invalidateQueries({
        queryKey: siteKeys.detail(variables.siteSlug),
      });
    },
  });
}
